import React, { Fragment, useState, useEffect } from "react";
import { func, bool, string, object, any } from "prop-types";
import moment from "moment";
import InputFile, { CompleteCheck } from "../../common/InputFile";
import { Button, PulseLoader } from "../../common";
import * as style from "./styles.module.scss";
import { openCv } from "../../helpers/helpers";
import OverflowedText from "../../common/OverflowedText";
import history from "../../history";
import { cvStatuses } from "../../helpers/cv";

export const CvFileIcon = ({ text = "ext", isBig = false }) => {
  const calcStyle = () => {
    if (text.length === 4) return { left: "2px", fontSize: "6px" };
    if (text.length === 1)
      return {
        fontSize: "16px",
        left: "8px",
        bottom: "2px",
      };
    return {};
  };

  return (
    <div
      className="position-relative"
      style={
        isBig
          ? {
              transform: "scale(3)",
              marginTop: "2.5rem",
              marginBottom: "2.5rem",
            }
          : {}
      }
    >
      <svg
        width="64"
        height="80"
        viewBox="0 0 64 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={style.editcv__icon}
      >
        <path
          d="M40 0H8C3.58 0 0.0400009 3.58 0.0400009 8L0 72C0 76.42 3.54 80 7.96 80H56C60.42 80 64 76.42 64 72V24L40 0ZM36 28V6L58 28H36Z"
          fill="black"
        />
      </svg>
      <span className={style.editcv__icontext} style={calcStyle()}>
        {text.toUpperCase()}
      </span>
    </div>
  );
};

CvFileIcon.propTypes = {
  text: string,
  isBig: bool,
};

const CvInputFile = ({
  styles = {},
  classes,
  onChangeHandler,
  idName,
  text = "Find file",
  acceptMimeTypes = "application/rtf,text/rtf,text/richtext,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  hasFileSelected = false,
  previewImage,
}) => {
  return (
    <div>
      <div style={{ position: "relative" }}>
        {hasFileSelected && (
          <Fragment>
            <div id={`${idName}-preview`} className="mb-1">
              <img
                src={`data:image/jpeg;base64,${previewImage}`}
                style={{ maxWidth: "100%" }}
              />
            </div>
            <CompleteCheck />
          </Fragment>
        )}
      </div>
      <label htmlFor={idName} className={classes} style={styles}>
        {text}
      </label>
      <input
        type="file"
        name="file"
        accept={acceptMimeTypes}
        onChange={(e) => onChangeHandler(e)}
        className="d-none"
        id={idName}
      />
    </div>
  );
};

CvInputFile.propTypes = {
  idName: string.isRequired,
  text: string,
  containerStyle: object,
  onChangeHandler: func.isRequired,
  acceptMimeTypes: string,
  styles: object,
  classes: string,
  previewImage: string,
  hasFileSelected: bool,
};

const EditCv = ({
  appData,
  isFileSelected,
  fileName,
  selectFile,
  handleSubmit,
  isCvLoading,
  validationError,
  errors,
  clearErrorsWithSource,
}) => {
  const [cvStatus, setCVStatus] = useState(cvStatuses.Empty);
  const navbarHeight =
    (document.getElementsByTagName("nav")[0] &&
      document.getElementsByTagName("nav")[0].offsetHeight) ||
    0;
  const hasExistingCv = appData.cv && appData.cv.fileKey;
  const existingCvName = appData.cv && appData.cv.fileName;
  const existingCvDate =
    existingCvName &&
    existingCvName.split("_")[1] &&
    moment(existingCvName.split("_")[1]);
  const cvErrors =
    errors && errors.filter((error) => error.source === "cv").length > 0;
  useEffect(() => {
    if (isFileSelected && cvStatus === cvStatuses.Empty) {
      setCVStatus(cvStatuses.FileSelected);
    } else if (cvErrors && !isCvLoading && cvStatus === cvStatuses.Loading) {
      setCVStatus(cvStatuses.Error);
    } else if (!cvErrors && !isCvLoading && cvStatus === cvStatuses.Loading) {
      setCVStatus(cvStatuses.Success);
    }
  }, [isFileSelected, errors, isCvLoading, validationError]);

  const handleSendCv = () => {
    setCVStatus(cvStatuses.Loading);
    clearErrorsWithSource("cv");
    handleSubmit();
  };

  switch (cvStatus) {
    case cvStatuses.Empty:
      return (
        <div
          className={`${style.editcv__wrapper}`}
          style={{ paddingTop: `${navbarHeight + 16}px` }}
        >
          <div className="w-100">
            <h5 className={`${style.editcv__title} mt-2 text-center`}>
              {hasExistingCv ? "Update your CV" : "Upload your CV"}
            </h5>
            {hasExistingCv || isCvLoading ? (
              <div className={`text-center ${style.editcv__description}`}>
                <p className="mb-0">
                  Review your current CV or upload a new one.
                </p>
                {existingCvDate && (
                  <div
                    className={`text-center ${style.editcv__description} d-flex flex-column align-items-center`}
                  >
                    Your current CV was added on the
                    <h3 className={`mb-0 mt-1 ${style.editcv__date}`}>
                      {existingCvDate.format("DD/MM/YYYY")}
                    </h3>
                  </div>
                )}
                <div className="mx-2">
                  <Button
                    onClick={() => openCv(appData.cv)}
                    disabled={isCvLoading}
                    classes="btn btn-outline-dark btn-block m-2 mx-auto font-weight-bold"
                    styles={{ width: 264 }}
                  >
                    {isCvLoading ? <PulseLoader isSmall /> : "Download a copy"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className={`${style.editcv__description} mx-3 text-center`}>
                Providing a CV or work history is really important to help us
                match you to the right opportunities.
              </p>
            )}
          </div>
          <div>
            <InputFile
              onChangeHandler={selectFile}
              classes="m-auto btn btn-block btn-primary cursor-pointer mb-0"
              styles={{ width: 264 }}
              idName="cvFile"
              text={hasExistingCv ? "Choose a new CV" : "Choose your CV"}
              acceptMimeTypes=".doc,.docx,.pdf"
            />
            <button
              className="btn mt-1 mx-auto btn-outline-dark btn-block font-weight-bold"
              style={{ width: 264 }}
              onClick={() => history.push("/profile")}
            >
              Cancel
            </button>
            <p className={`${style.editcv__smalldescription} mx-4 mt-1 mb-1`}>
              Your CV needs to be provided as <b>doc</b>, <b>docx</b> or{" "}
              <b>PDF</b> files.
            </p>
          </div>
        </div>
      );
    case cvStatuses.FileSelected:
      return (
        <div
          className={`${style.editcv__wrapper}`}
          style={{ paddingTop: `${navbarHeight + 16}px` }}
        >
          <div className="w-100">
            <h5 className={`${style.editcv__title} mt-2 text-center`}>
              {hasExistingCv ? "Update your CV" : "Upload your CV"}
            </h5>
            {validationError === "formatInvalid" ? (
              <div
                className={`d-flex flex-column align-items-center p-1 mx-1 ${style.editcv__selected} mx-auto bg-white`}
              >
                <CvFileIcon text={"!"} />
                <span className={style.editcv__filename}>Wrong file type</span>
                <p
                  className={`${style.editcv__smalldescription} mx-1 mt-0 mb-0`}
                >
                  Your CV needs to be provided as <b>doc</b>, <b>docx</b> or{" "}
                  <b>PDF</b> files.
                </p>
              </div>
            ) : (
              <div
                className={`d-flex flex-column align-items-center p-1 mx-1 ${style.editcv__selected} mx-auto`}
              >
                <CvFileIcon text={fileName.split(".")[1]} />
                <span className={style.editcv__filename}>
                  <OverflowedText overflowedText={fileName}>
                    {fileName}
                  </OverflowedText>
                </span>
                <div className="col-12 col-sm-4 px-0 float-left mw-100">
                  <InputFile
                    onChangeHandler={selectFile}
                    classes={`btn btn-block btn-outline-info bg-white px-0 mb-0 ${style.editcv__changefile}`}
                    idName="cvFile"
                    text="Change file"
                    acceptMimeTypes=".doc,.docx,.pdf"
                  />
                </div>
              </div>
            )}
          </div>
          <div>
            {validationError === "formatInvalid" ? (
              <InputFile
                onChangeHandler={selectFile}
                classes="btn btn-primary btn-block mt-2 mx-auto mb-1"
                styles={{ width: 264 }}
                idName="cvFile"
                text={"Choose a different file"}
                acceptMimeTypes=".doc,.docx,.pdf"
              />
            ) : (
              <Button
                onClick={handleSendCv}
                classes="btn btn-primary btn-block mt-2 mx-auto mb-1"
                styles={{ width: 264 }}
              >
                {hasExistingCv ? "Upload your new CV" : "Upload your CV"}
              </Button>
            )}
            <button
              className="btn mt-1 mx-auto btn-outline-dark btn-block font-weight-bold mb-1"
              style={{ width: 264 }}
              onClick={() => history.push("/profile")}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    case cvStatuses.Error:
      return (
        <div
          className={`${style.editcv__wrapper}`}
          style={{ paddingTop: `${navbarHeight + 16}px` }}
        >
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <div className="w-100 d-flex flex-column justify-content-center align-items-center">
              <img src="/images/icons/timesheet-error.svg" />
              <h4 className={`mt-2 text-center ${style.editcv__title}`}>
                Upload failed
              </h4>
              <span className="text-center">
                There has been an error uploading your CV.
              </span>
              <span className="text-center">
                Please click the button below to retry.
              </span>
            </div>
          </div>
          <div>
            <button
              className="btn btn-primary font-weight-bold mt-2 mx-auto mb-1"
              onClick={handleSendCv}
              style={{ width: 264 }}
            >
              Try again
            </button>
            <button
              className="btn mx-auto btn-outline-dark btn-block font-weight-bold mb-1"
              style={{ width: 264 }}
              onClick={() => history.push("/profile")}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    case cvStatuses.Success:
      return (
        <div
          className={`${style.editcv__wrapper}`}
          style={{ paddingTop: `${navbarHeight + 16}px` }}
        >
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <div className="w-100 d-flex flex-column justify-content-center align-items-center">
              <img src="/images/icons/timesheet-approve.svg" />
              <h4 className={`mt-2 text-center ${style.editcv__title}`}>
                CV uploaded successfully
              </h4>
              <span className="text-center mx-2">
                Your CV has been successfully uploaded. You can easily update
                this file in the future if your information changes.
              </span>
            </div>
          </div>
          <div>
            <button
              className="btn btn-primary font-weight-bold mt-2 mx-auto mb-1"
              onClick={() => history.push("/profile")}
              style={{ width: 264 }}
            >
              Back to your account
            </button>
          </div>
        </div>
      );
    case cvStatuses.Loading:
      return (
        <div
          className={`${style.editcv__wrapper}`}
          style={{ paddingTop: `${navbarHeight + 16}px` }}
        >
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <PulseLoader />
          </div>
        </div>
      );
    default:
      return <Fragment>default</Fragment>;
  }
};

EditCv.propTypes = {
  selectFile: func.isRequired,
  handleSubmit: func.isRequired,
  isFileSelected: bool.isRequired,
  description: string.isRequired,
  fileName: string.isRequired,
  appData: object.isRequired,
  isCvLoading: bool.isRequired,
  validationError: string,
  errors: any,
  clearErrorsWithSource: func,
};

export default EditCv;
